import { useState } from "react";
import { Requirement } from "../../types/requirement.response";
import { AddableCourse, CourseLite, CourseOption } from "../../types/course.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { DEFAULT_SEMESTER } from "../../constants";
import { useSuggestedCourses } from "../../hooks/useSuggestedCourses";
import { useHydratedCourses } from "../../hooks/useHydratedCourses";
import CourseRow from "../course-row/CourseRow";
import { CourseRowSkeletonRows } from "../loading/CourseRowSkeleton";
import ChevronIcon from "../icons/ChevronIcon";

const INITIAL_LIMIT = 3;

interface SuggestedCourseRowsProps {
    programId: string;
    requirement: Requirement;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void;
    handleDeleteCourse: (deletedCourse: CourseLite) => void;
    handleFavoriteCourse: (courseOption: CourseLite) => void;
    optimisticFavorites: Map<string, boolean>;
    userCourses?: CourseOption[];
    /** 初始展示条数;"Show more" 展开全部(接口默认 10 条)。默认 3,detail 页可给大值 */
    initialLimit?: number;
}

/**
 * Broad(内涵)requirement 卡片的"推荐"段(<tr> 行,嵌入调用方的 <tbody>):
 * suggestions 端点的合格课示例,可直接加课。加载中渲染骨架行;
 * 失败或空结果整段隐藏(推荐是辅助信息,不值得一条错误提示)。
 */
export default function SuggestedCourseRows({
    programId,
    requirement,
    handleAddCourse,
    handleDeleteCourse,
    handleFavoriteCourse,
    optimisticFavorites,
    userCourses,
    initialLimit = INITIAL_LIMIT,
}: SuggestedCourseRowsProps) {
    const { status, courses } = useSuggestedCourses(programId, requirement, userCourses);
    const [expanded, setExpanded] = useState(false);
    // 展示数据在 useSuggestedCourses 里已随 batch 到位,这里命中缓存零请求
    const hydrated = useHydratedCourses(courses);

    if (status === "idle" || status === "error" || (status === "loaded" && courses.length === 0)) {
        return null;
    }

    const visibleCourses = expanded ? courses : courses.slice(0, initialLimit);
    const hiddenCount = courses.length - visibleCourses.length;

    return (
        <>
            <tr>
                <td colSpan={4} className="pt-4 pb-1">
                    <div className="flex items-baseline gap-2">
                        <span className="text-sm font-semibold text-gray-700">Suggested courses</span>
                        <span className="text-xs text-gray-400">
                            examples that count toward this requirement — search to find more
                        </span>
                    </div>
                </td>
            </tr>
            {status === "loading" ? (
                <CourseRowSkeletonRows rows={initialLimit} />
            ) : (
                <>
                    {visibleCourses.map(summary => (
                        <CourseRow
                            key={summary.mapKey}
                            course={summary}
                            hydrated={hydrated.get(summary.mapKey)}
                            selectedSemester={DEFAULT_SEMESTER}
                            onAddToSchedule={(course, classSectionIds) => handleAddCourse(course, [], DEFAULT_SEMESTER, classSectionIds)}
                            onMarkAsTaken={(course) => handleAddCourse(course, [], null)}
                            onDeleteFromSchedule={(course) => handleDeleteCourse(course)}
                            onFavoriteToggle={(course) => handleFavoriteCourse(course)}
                            optimisticFavorites={optimisticFavorites}
                            userCourses={userCourses}
                            requirementId={requirement.info.id}
                        />
                    ))}
                    {hiddenCount > 0 && (
                        <tr>
                            <td colSpan={4} className="py-2 border-b border-gray-50">
                                <button
                                    onClick={() => setExpanded(true)}
                                    className="flex items-center justify-center w-full gap-1 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <ChevronIcon direction="down" className="w-4 h-4" />
                                    <span>Show more suggestions</span>
                                </button>
                            </td>
                        </tr>
                    )}
                </>
            )}
        </>
    );
}
